import * as React from 'react';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { questionFieldName } from '../../../../core/constants';
import RadioGroupQuestion from '../../../../commonComponents/fields/RadioGroupQuestion';
import { FieldProps } from '../../../../core/form';
import { HelpSection, Question } from './questionData';
import CheckListQuestion from '../../../../commonComponents/fields/CheckListQuestion';
import SliderQuestion from '../../../../commonComponents/fields/SliderQuestion';
import { QuestionTypes } from '../../../../core/enums';
import FieldDecorator from '../../../../commonComponents/fields/FieldDecorator';
import { evaluateVisibility } from '../../../../core/domain';
import DragAndDropQuestion from '../../../../commonComponents/fields/dragAndDrop/DragAndDropQuestion';
import { popupZIndex, slateGrey, whiteColour } from '../../../cssConstants';

const QuestionField = (props: Props) => {
	const { questionSpec, onRadioQuestionChange } = props;
	const { type, title, subTitle, sliderTitle, options, helpSection } = questionSpec;
	const {
		formState: { errors }
	} = useFormContext();

	const visibleOptions = options.filter((x) => evaluateVisibility(x.visible));

	return (
		<>
			<OneColumnField
				name={questionFieldName}
				className="questionField"
				errors={errors}
				label={title}
				subLabel={subTitle}
				customErrorMessage={questionErrorMessage}
				$showLightLabel={type === QuestionTypes.Slider}
				defaultFormField={false}
			>
				{type === QuestionTypes.RadioList && (
					<RadioGroupQuestion
						name={questionFieldName}
						data={visibleOptions}
						required
						onChange={onRadioQuestionChange}
					/>
				)}
				{type === QuestionTypes.CheckList && (
					<CheckListQuestion name={questionFieldName} data={visibleOptions} required />
				)}
				{type === QuestionTypes.Slider && (
					<SliderQuestion sliderTitle={sliderTitle} name={questionFieldName} data={visibleOptions} required />
				)}
				{type === QuestionTypes.DragAndDrop && (
					<DragAndDropQuestion
						label={title}
						name={questionFieldName}
						data={visibleOptions}
						sourceColumnTitle={questionSpec.sourceColumnTitle}
						destinationColumnTitle={questionSpec.destinationColumnTitle}
						required
					/>
				)}
			</OneColumnField>
			{helpSection && <QuestionHelpSection title={helpSection.title} content={helpSection.content} />}
		</>
	);
};

interface Props extends FieldProps {
	questionSpec: Question;
	onRadioQuestionChange?: () => void;
}

export default QuestionField;

const questionErrorMessage = 'Please select at least one option';

const OneColumnField = styled(FieldDecorator)<{ $showLightLabel: boolean }>`
	display: block;
	width: 100%;

	&.questionField {
		> label {
			font-weight: ${(props) => (props.$showLightLabel ? 'normal' : '500')};
			font-size: ${(props) => (props.$showLightLabel ? '16px' : '18px')};
			color: ${slateGrey};
		}
	}
`;

const QuestionHelpSection = (props: HelpSection) => {
	return (
		<StyledQuestionHelp>
			<h2>
				<em>{props.title}</em>
			</h2>
			{props.content && (
				<ul>
					{props.content.map((x) => (
						<li key={x}>
							<em>{x}</em>
						</li>
					))}
				</ul>
			)}
		</StyledQuestionHelp>
	);
};

const StyledQuestionHelp = styled.section`
	cursor: help;
	margin: 20px 8px;
	position: relative;

	h2 {
		display: inline-block;
		margin: 15px 0;
		font-size: 16px;
		line-height: 19px;
		color: #606060;
		border-bottom: 1px dotted #606060;
	}

	ul {
		display: none;
		list-style-type: none;
		padding: 20px;
		position: absolute;
		color: ${whiteColour};
		background-color: ${(props) => props.theme.brandingColourHeavy};
		border-radius: 10px;
		top: 35px;
		z-index: ${popupZIndex};

		li {
			font-size: 16px;
			line-height: 19px;
			margin: 8px 0;
		}

		&::before {
			content: '';
			display: block;
			position: absolute;
			top: -10px;
			left: 20px;
			width: 0;
			height: 0;
			border: solid transparent 5px;
			border-bottom-color: ${(props) => props.theme.brandingColourHeavy};
		}
	}

	&:hover ul,
	&:active ul {
		display: block;
	}
`;
